import { useCallback, useEffect, useRef, useState } from 'react';
import { API_CONFIG, buildApiUrl } from '../config/api';

// How often each feed is re-polled. Earnings are cached 60s on the backend and
// price 5min, so polling faster than this only burns requests.
const EARNINGS_POLL_MS = 60000;
const PRICE_POLL_MS = 300000;

export interface HourlyBucket {
  hoursAgo: number;
  total: number;
}

interface EarningsWindow {
  hours: number;
  total: number;
  count: number;
  complete: boolean;
}

export interface EarningsView {
  loading: boolean;
  error: string | null;
  /** DOGE figures, measured from real pool credits. */
  lastHour: number | null;
  last24h: number | null;
  ratePerHour: number | null;
  projectedDaily: number | null;
  hourly: HourlyBucket[];
  /** True only when the pool history actually spans a full 24h. */
  complete24h: boolean;
  /** Seconds since the most recent credit landed. */
  stalenessSeconds: number | null;
  /** Fiat conversions. Null until a price arrives — never guessed. */
  price: { mxn: number | null; usd: number | null };
  mxn: {
    lastHour: number | null;
    last24h: number | null;
    perHour: number | null;
    projectedDaily: number | null;
  };
  refresh: () => void;
}

const num = (v: any): number | null => (typeof v === 'number' && isFinite(v) ? v : null);
const mul = (a: number | null, b: number | null): number | null =>
  a === null || b === null ? null : a * b;

async function getJson(url: string, signal: AbortSignal) {
  const res = await fetch(url, { signal, headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * Fetches measured DOGE earnings plus a fiat rate and exposes both the crypto
 * and MXN views. Every value is sourced from the backend; nothing is
 * extrapolated here except projectedDaily, which the backend labels as such.
 */
export function useEarnings(crypto: string = 'doge'): EarningsView {
  const [earnings, setEarnings] = useState<any>(null);
  const [price, setPrice] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const data = await getJson(
          buildApiUrl(API_CONFIG.endpoints.earnings(crypto)),
          controller.signal
        );
        if (!mounted.current) return;
        setEarnings(data);
        setError(null);
      } catch (e: any) {
        if (!mounted.current || e?.name === 'AbortError') return;
        setError(e?.message || 'Failed to load earnings');
      } finally {
        if (mounted.current) setLoading(false);
      }
    };

    load();
    const id = setInterval(load, EARNINGS_POLL_MS);
    return () => {
      controller.abort();
      clearInterval(id);
    };
  }, [crypto, tick]);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const data = await getJson(
          `${buildApiUrl(API_CONFIG.endpoints.price(crypto))}?vs=mxn,usd`,
          controller.signal
        );
        if (!mounted.current) return;
        setPrice(data);
      } catch (e: any) {
        // A missing price must never blank the DOGE figures, so it is silent.
        if (e?.name !== 'AbortError') console.warn('price fetch failed:', e?.message);
      }
    };

    load();
    const id = setInterval(load, PRICE_POLL_MS);
    return () => {
      controller.abort();
      clearInterval(id);
    };
  }, [crypto, tick]);

  const last1h: EarningsWindow | null = earnings?.last1h ?? null;
  const last24: EarningsWindow | null = earnings?.last24h ?? null;

  const lastHour = last1h ? num(last1h.total) : null;
  const last24h = last24 ? num(last24.total) : null;
  const ratePerHour = num(earnings?.ratePerHour);
  const projectedDaily = num(earnings?.projectedDaily);
  const mxnRate = num(price?.rates?.mxn);

  return {
    loading,
    error,
    lastHour,
    last24h,
    ratePerHour,
    projectedDaily,
    hourly: Array.isArray(earnings?.hourly) ? earnings.hourly : [],
    complete24h: Boolean(last24?.complete),
    stalenessSeconds: num(earnings?.stalenessSeconds),
    price: { mxn: mxnRate, usd: num(price?.rates?.usd) },
    mxn: {
      lastHour: mul(lastHour, mxnRate),
      last24h: mul(last24h, mxnRate),
      perHour: mul(ratePerHour, mxnRate),
      projectedDaily: mul(projectedDaily, mxnRate)
    },
    refresh
  };
}

export default useEarnings;
